import i18n from "@/i18n";
import moment from "moment";
import type { ActionTree, GetterTree } from "vuex";
import type { IDataState } from "@/store/modules/data";
import { DataModules } from "@/store/modules/data/modules";
import type { ApiPathGetter } from "@upmind-automation/types";
import type { IClient } from "@upmind-automation/types";
import type { IClientDataExport } from "@/models/clientDataExports";
import type { IState } from "@/store";
import { Methods } from "@upmind-automation/types";
import { ToastProgrammatic as $toast } from "buefy";
import { convertObjAb2Json, downloadBlob } from "@/helpers/file";

/**
 * Authority client data export.
 *
 * Admin-only: staff request a PDF holding a client's full record (details,
 * emails, phones, addresses, companies) plus every service, generated in the
 * background, then download it once the export is finished.
 */

const initialState = {} as IDataState;

const getters: GetterTree<IDataState, IState> = {
  apiPath: (): ApiPathGetter => (clientId: IClient["id"]) => {
    // Staff-only feature — there is no client-context equivalent.
    const admin = `api/admin/clients/${clientId}/data_exports`;
    return { admin };
  }
};

const actions: ActionTree<IDataState, IState> = {
  list: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/list",
      {
        ...payload,
        path: getters.apiPath(payload?.id).admin,
        storeModule: DataModules.CLIENTS_DATA_EXPORTS
      },
      { root: true }
    );
  },
  create: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/create",
      {
        ...payload,
        path: getters.apiPath(payload?.id).admin,
        storeModule: DataModules.CLIENTS_DATA_EXPORTS
      },
      { root: true }
    );
  },
  download: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/callApi",
      {
        ...payload,
        method: Methods.GET,
        returnData: false,
        path: `${getters.apiPath(payload?.id).admin}/${
          payload?.exportId
        }/download`,
        storeModule: DataModules.CLIENTS_DATA_EXPORTS,
        requestConfig: { params: payload?.params, responseType: "arraybuffer" }
      },
      { root: true }
    );
  },
  tryDownload: async ({ dispatch }, dataExport: IClientDataExport) => {
    try {
      const data = await dispatch("download", {
        id: dataExport.client_id,
        exportId: dataExport.id
      });
      const blob = new Blob([data], { type: "application/pdf;" });
      const date = moment(
        dataExport.completed_at || dataExport.created_at
      ).format("YYYY-MM-DD");
      downloadBlob(
        blob,
        `authority-client-data-export-${dataExport.client_id}-${date}.pdf`
      );
      return true;
    } catch (error) {
      // The response is an ArrayBuffer — convert it so the API message
      // (e.g. "This export has expired.") can be surfaced.
      convertObjAb2Json(error as any, "response.data");
      dispatch("api/handleValidationError", { error }, { root: true });
      return false;
    }
  },
  // Requests a fresh export. The API rejects the request with a 409 while
  // another export is still generating for the same client.
  request: async ({ dispatch, rootGetters }, { clientId, vm }) => {
    if (!rootGetters["user/can"]("create_client_data_export")) return;
    try {
      const dataExport = await dispatch("create", { id: clientId, vm });
      $toast.open({
        message: i18n.t("_sentence.client_data_export.requested_msg") as string
      });
      return dataExport;
    } catch (error) {
      if (rootGetters["api/is409"](error)) {
        $toast.open({
          type: "is-warning",
          message: i18n.t(
            "_sentence.client_data_export.in_progress_msg"
          ) as string
        });
        return;
      }
      dispatch("api/handleValidationError", { error }, { root: true });
      return;
    }
  },
  openClientDataExportsModal: ({ dispatch }, payload) => {
    return dispatch(
      "ui/open/modal",
      {
        config: {
          component: () =>
            import("@/components/app/global/client/clientDataExportsModal.vue"),
          ...payload,
          width: 640
        }
      },
      { root: true }
    );
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions
};
